import { ConfirmDialog, toast } from '@nexus/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useOfflineState } from '../offline/offline';
import { deleteAllStores } from '../offline/store';
import { forgetRecentHits } from '../search/recent';
import { forgetPushDevice } from '../settings/push-device';

export function useSignOut(): () => Promise<void> {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    // What the browser keeps goes first, also when the server cannot be reached: offline data
    // never outlives the sign-out (ADR-0013).
    client.clear();
    forgetRecentHits();
    await deleteAllStores();
    // A shared device gets no more notices for this person; needs the session, so before logout.
    await forgetPushDevice();
    try {
      await api.post('/auth/logout');
    } catch {
      toast.error(t('shell.profile.signOutFailed'));
      return;
    }
    await navigate({ to: '/login' });
  };
}

/**
 * Sign-out that first asks when changes or files made offline have not reached the server:
 * signing out deletes them with the rest of the offline data.
 */
export function useGuardedSignOut(): [request: () => void, dialog: ReactNode] {
  const { t } = useTranslation();
  const signOut = useSignOut();
  const { changes, uploads } = useOfflineState();
  const unsent =
    changes.filter((c) => c.state !== 'rejected').length +
    uploads.filter((u) => u.state !== 'failed').length;
  const [open, setOpen] = useState(false);
  const request = () => (unsent ? setOpen(true) : void signOut());
  const dialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      tone="danger"
      title={t('shell.sync.signOutPendingTitle')}
      description={t('shell.sync.signOutPending', { count: unsent })}
      confirmLabel={t('shell.sync.signOutAnyway')}
      onConfirm={signOut}
    />
  );
  return [request, dialog];
}
